import { z } from 'zod';
import { garmentTypeKeySchema } from '../constants/garment-types.js';
import { userRoleSchema } from '../constants/roles.js';
import { staffShiftSchema } from './attendance-hr.js';
import { craftRoleKeySchema } from './craft-roles.js';
import { PAYABLE_STAGES, payableStageSchema } from '../constants/stages.js';
import { nonNegativePaiseSchema } from '../money/paise.js';
import { seatUsageSchema } from './billing.js';
import {
  dateInputSchema,
  emailSchema,
  indianMobileSchema,
  isoDateStringSchema,
  objectIdSchema,
  passwordSchema,
  pinSchema,
} from './common.js';

/**
 * The people who do the work, and what each of them is paid per piece (task 19).
 *
 * Two shapes matter here and they are deliberately separate:
 *
 *  - THE ROSTER. A staff member is a global user (task 3) holding a membership of this shop;
 *    what this module adds is the invite that brings them in, the specialisation that says
 *    which stages they normally handle, and deactivation that stops them signing in WITHOUT
 *    deleting a single row of their history.
 *
 *  - THE RATE CARD. A rate is defined per garment type and payable stage — shop-wide by
 *    default, overridden per staff member only where it differs — and every rate carries an
 *    `effectiveFrom` date. Rates are never edited in place: a change is a NEW dated entry, so
 *    a job assigned in March settles at March's rate even if the rate rose in April.
 */

// --- Roles a shop can bring in -------------------------------------------------------------

/** Roles an invite can carry. Ownership is transferred, never invited. */
export const INVITABLE_ROLES = ['manager', 'tailor'] as const;
export type InvitableRole = (typeof INVITABLE_ROLES)[number];
export const invitableRoleSchema = z.enum(INVITABLE_ROLES);

/** How long an invite stays open. A week covers "I'll set you up when you start on Monday". */
export const STAFF_INVITE_TTL_DAYS = 7;

export const STAFF_INVITE_STATUSES = ['pending', 'accepted', 'revoked'] as const;
export type StaffInviteStatus = (typeof STAFF_INVITE_STATUSES)[number];
export const staffInviteStatusSchema = z.enum(STAFF_INVITE_STATUSES);

const staffNameSchema = z.string().trim().min(2, 'Enter a name').max(120);

/**
 * The trade a person practises, as a key into the shop's own list (`schemas/craft-roles.ts`).
 *
 * A LABEL, and never anything more. It is not the auth `role` above, it is not derived from it,
 * and no permission check anywhere reads it. It is not `specialisations` either: those are the
 * assignment hint, and this changes nothing about how work is given out.
 */
export const staffCraftRoleSchema = craftRoleKeySchema;

/** The stages a person normally handles. Order is not meaningful; duplicates are a mis-tap. */
export const specialisationsSchema = z
  .array(payableStageSchema)
  .max(PAYABLE_STAGES.length)
  .refine((stages) => new Set(stages).size === stages.length, 'Each stage may appear once');

// --- Inviting ------------------------------------------------------------------------------

export const inviteStaffSchema = z.object({
  email: emailSchema,
  name: staffNameSchema,
  /** Optional contact number for the shop floor. Never a credential. */
  phone: indianMobileSchema.optional(),
  role: invitableRoleSchema.default('tailor'),
  specialisations: specialisationsSchema.default([]),
  /**
   * What the shop calls their trade. Optional — an invitation is already four questions, and
   * this one can be answered later from their profile — and carried onto the profile the moment
   * they join, so nobody has to re-enter it.
   */
  craftRole: staffCraftRoleSchema.optional(),
});
export type InviteStaffInput = z.input<typeof inviteStaffSchema>;

// --- Joining (the invited person's side; no session yet) ------------------------------------

const otpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Enter the 6-digit code from the email');

export const staffJoinStartSchema = z.object({
  email: emailSchema,
});

export const staffJoinVerifySchema = z.object({
  email: emailSchema,
  code: otpCodeSchema,
});

/**
 * The final join step. A tailor sets a 4-digit PIN, a manager sets a password — matching how
 * each signs in from then on. Either credential is optional here because the person may
 * already work at another shop on this same address and already hold one; the server requires
 * the right credential only when they have none.
 */
export const staffJoinCompleteSchema = z.object({
  verificationToken: z.string().min(1, 'Verify your email address first'),
  name: staffNameSchema,
  pin: pinSchema.optional(),
  password: passwordSchema.optional(),
});
export type StaffJoinCompleteInput = z.input<typeof staffJoinCompleteSchema>;

// --- Editing the roster ----------------------------------------------------------------------

export const updateStaffSchema = z
  .object({
    specialisations: specialisationsSchema.optional(),
    /** Changing a role is owner-only and audited — see `staff.role.change` on the matrix. */
    role: invitableRoleSchema.optional(),
    /**
     * The trade label. `null` clears it. Guarded by the ordinary `staff.write` permission that
     * already covers editing somebody's specialisations, because it grants nothing: changing it
     * cannot widen or narrow what that person may do.
     */
    craftRole: staffCraftRoleSchema.nullish(),
    /**
     * This person's own working hours. `null` puts them back on the shop's. Guarded by the same
     * `staff.write` that already covers their specialisations, because it grants nothing —
     * changing when somebody is expected in cannot widen or narrow what they may do. What it
     * DOES change is whether they are marked late, which is why it is audited.
     */
    shift: staffShiftSchema.nullish(),
  })
  .refine(
    (value) =>
      value.specialisations !== undefined ||
      value.role !== undefined ||
      value.craftRole !== undefined ||
      value.shift !== undefined,
    { message: 'Nothing to update' },
  );
export type UpdateStaffInput = z.input<typeof updateStaffSchema>;

// --- The rate card ---------------------------------------------------------------------------

/** A shop-wide default rate. Defaults are the floor, so a default can never be "no rate". */
export const setDefaultRateSchema = z.object({
  garmentTypeKey: garmentTypeKeySchema,
  stage: payableStageSchema,
  ratePaise: nonNegativePaiseSchema,
  /** When this rate starts to apply. Defaults to now; dating is what protects history. */
  effectiveFrom: dateInputSchema.optional(),
});
export type SetDefaultRateInput = z.input<typeof setDefaultRateSchema>;

/**
 * A per-staff override. `ratePaise: null` CLEARS the override from `effectiveFrom` onwards —
 * the person goes back to the shop default without rewriting what the override used to say.
 */
export const setStaffRateSchema = z.object({
  garmentTypeKey: garmentTypeKeySchema,
  stage: payableStageSchema,
  ratePaise: nonNegativePaiseSchema.nullable(),
  effectiveFrom: dateInputSchema.optional(),
});
export type SetStaffRateInput = z.input<typeof setStaffRateSchema>;

export const rateQuerySchema = z.object({
  /** The date to resolve rates AT. Defaults to now — history stays answerable. */
  at: dateInputSchema.optional(),
});

// --- Time rates (task 19) ----------------------------------------------------------------------

/**
 * What KIND of pay a rate-card row states.
 *
 * A `piece` row prices one garment type at one stage — the only kind that existed before task
 * 19, and the kind every stored row without this field still is. A `time` row prices somebody's
 * TIME instead: an hourly rate for a shop paying by recorded hours, a monthly salary for a shop
 * paying a fixed wage. It carries no garment and no stage, which is exactly why it can never be
 * returned by a piece lookup: that query always names a real garment key.
 *
 * Both kinds live in the same insert-only dated collection because they are the same fact — what
 * a shop agreed to pay, from when — and a salary change must not rewrite last month's payroll
 * for the same reason a piece-rate change must not rewrite last month's settlements.
 */
export const RATE_CARD_KINDS = ['piece', 'time'] as const;
export type RateCardKind = (typeof RATE_CARD_KINDS)[number];
export const rateCardKindSchema = z.enum(RATE_CARD_KINDS);

/**
 * The shop-wide time rate: what an hour is worth, and what a full calendar month is worth.
 *
 * At least one figure is required — a row stating neither prices nothing — and neither is
 * nullable here for the same reason `setDefaultRateSchema`'s `ratePaise` is not: a default is
 * the floor everybody falls back to, so "no rate" is not a default a shop can set.
 */
export const setDefaultTimeRateSchema = z
  .object({
    /** Paise per worked hour. Omitted leaves the shop without an hourly basis. */
    hourlyRatePaise: nonNegativePaiseSchema.optional(),
    /** Paise for a full calendar month, pro-rated by attendance over a shorter cycle. */
    monthlySalaryPaise: nonNegativePaiseSchema.optional(),
    /** When this rate starts to apply. Defaults to now; dating is what protects history. */
    effectiveFrom: dateInputSchema.optional(),
  })
  .refine(
    (value) => value.hourlyRatePaise !== undefined || value.monthlySalaryPaise !== undefined,
    'Set an hourly rate, a monthly salary, or both',
  );
export type SetDefaultTimeRateInput = z.input<typeof setDefaultTimeRateSchema>;

/**
 * A per-staff time override. Both figures `null` CLEARS the override from `effectiveFrom`
 * onwards — the person goes back to the shop default without rewriting what the override used
 * to say, exactly as `setStaffRateSchema`'s `ratePaise: null` does for a piece rate.
 */
export const setStaffTimeRateSchema = z
  .object({
    hourlyRatePaise: nonNegativePaiseSchema.nullable().optional(),
    monthlySalaryPaise: nonNegativePaiseSchema.nullable().optional(),
    effectiveFrom: dateInputSchema.optional(),
  })
  .refine(
    (value) => value.hourlyRatePaise !== undefined || value.monthlySalaryPaise !== undefined,
    'Set an hourly rate, a monthly salary, or clear the override with both set to null',
  );
export type SetStaffTimeRateInput = z.input<typeof setStaffTimeRateSchema>;

// --- Views ------------------------------------------------------------------------------------

export const staffMemberSchema = z.object({
  userId: objectIdSchema,
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  role: userRoleSchema,
  active: z.boolean(),
  joinedAt: isoDateStringSchema,
  specialisations: z.array(payableStageSchema),
  /** The stored key, or `null`. Sits beside `role` and means nothing about permissions. */
  craftRole: z.string().nullable(),
  /**
   * This person's own hours, when they differ from the shop's. `null` — which is almost
   * everybody — means the shop's workday applies, and that is deliberate: making every shop
   * fill in forty shift rows to express "the same as the shop, except him" is a screen nobody
   * completes. See `resolveShift`.
   */
  shift: staffShiftSchema.nullable(),
  /**
   * The label that key resolves to through the shop's list. `null` when unset — and `null`
   * rather than an error when the key is no longer configured, because a shop deleting a role
   * it stopped hiring for must not turn every roster read into a 500.
   */
  craftRoleLabel: z.string().nullable(),
});
export type StaffMember = z.infer<typeof staffMemberSchema>;

export const staffSeatsSchema = z.object({
  /** People who can sign in today, including the owner. What a plan's seat allowance gates. */
  activeCount: z.number().int().nonnegative(),
  /** The plan's allowance. `null` means unlimited. Enforcement is task 23's; showing is ours. */
  allowance: z.number().int().positive().nullable(),
  /**
   * The full usage figure task 23 enforces against, including invitations not yet accepted.
   * Optional because a tailor's own one-row roster has no shop-wide count to report.
   */
  usage: seatUsageSchema.optional(),
});
export type StaffSeats = z.infer<typeof staffSeatsSchema>;

export const staffListSchema = z.object({
  items: z.array(staffMemberSchema),
  seats: staffSeatsSchema,
});
export type StaffList = z.infer<typeof staffListSchema>;

export const staffInviteSchema = z.object({
  id: objectIdSchema,
  email: z.string(),
  phone: z.string().nullable(),
  name: z.string(),
  role: invitableRoleSchema,
  specialisations: z.array(payableStageSchema),
  /** As on a member: the stored key, and what the shop's list currently calls it. */
  craftRole: z.string().nullable(),
  craftRoleLabel: z.string().nullable(),
  status: staffInviteStatusSchema,
  expiresAt: isoDateStringSchema,
  invitedByName: z.string().nullable(),
  createdAt: isoDateStringSchema,
});
export type StaffInvite = z.infer<typeof staffInviteSchema>;

export const staffInviteListSchema = z.object({
  items: z.array(staffInviteSchema),
});

/** Where a resolved rate came from — the fallback chain, made visible. */
export const RATE_SOURCES = ['staff', 'shop_default'] as const;
export type RateSource = (typeof RATE_SOURCES)[number];
export const rateSourceSchema = z.enum(RATE_SOURCES);

export const resolvedRateSchema = z.object({
  garmentTypeKey: z.string(),
  /** The shop's own label for the garment, so the matrix reads in the shop's words. */
  garmentTypeLabel: z.string(),
  stage: payableStageSchema,
  /** `null` when nothing is defined for this combination yet. */
  ratePaise: nonNegativePaiseSchema.nullable(),
  source: rateSourceSchema.nullable(),
  effectiveFrom: isoDateStringSchema.nullable(),
});
export type ResolvedRate = z.infer<typeof resolvedRateSchema>;

export const rateCardEntrySchema = z.object({
  id: objectIdSchema,
  /** `null` on a shop default. */
  staffUserId: objectIdSchema.nullable(),
  /** `null` on a `time` row, which prices somebody's hours rather than a garment. */
  garmentTypeKey: z.string().nullable(),
  /** `null` on a `time` row, for the same reason. */
  stage: payableStageSchema.nullable(),
  /** `null` on an entry that cleared a staff override. */
  ratePaise: nonNegativePaiseSchema.nullable(),
  /**
   * Absent on every row written before task 19 — all of which are piece rates, which is what
   * the server defaults them to. Optional here so a stored entry from before this field existed
   * still parses without a backfill.
   */
  kind: rateCardKindSchema.optional(),
  /** Paise per worked hour on a `time` row; `null` (or absent) everywhere else. */
  hourlyRatePaise: nonNegativePaiseSchema.nullable().optional(),
  /** Paise for a full calendar month on a `time` row; `null` (or absent) everywhere else. */
  monthlySalaryPaise: nonNegativePaiseSchema.nullable().optional(),
  effectiveFrom: isoDateStringSchema,
  setByName: z.string().nullable(),
  createdAt: isoDateStringSchema,
});
export type RateCardEntry = z.infer<typeof rateCardEntrySchema>;

/**
 * What somebody's time resolves to at a date, and which rung of the chain answered.
 *
 * Both figures are independently `null`: a shop paying by the hour configures an hourly rate and
 * no salary, and a shop paying a monthly wage does the reverse. `null` means "this shop has not
 * configured one", never "free".
 */
export const resolvedTimeRateSchema = z.object({
  /** `null` when this is the shop's own default rather than one person's. */
  staffUserId: objectIdSchema.nullable(),
  /** The date the rate was resolved at, echoed back. */
  at: isoDateStringSchema,
  hourlyRatePaise: nonNegativePaiseSchema.nullable(),
  monthlySalaryPaise: nonNegativePaiseSchema.nullable(),
  source: rateSourceSchema.nullable(),
  effectiveFrom: isoDateStringSchema.nullable(),
});
export type ResolvedTimeRate = z.infer<typeof resolvedTimeRateSchema>;

export const staffRatesSchema = z.object({
  staffUserId: objectIdSchema,
  /** The date the matrix was resolved at, echoed back. */
  at: isoDateStringSchema,
  matrix: z.array(resolvedRateSchema),
  /** This person's own dated override entries, newest first. */
  entries: z.array(rateCardEntrySchema),
});
export type StaffRates = z.infer<typeof staffRatesSchema>;

export const defaultRatesSchema = z.object({
  at: isoDateStringSchema,
  matrix: z.array(resolvedRateSchema),
  entries: z.array(rateCardEntrySchema),
});
export type DefaultRates = z.infer<typeof defaultRatesSchema>;
